// src/components/SearchBar.js
import React, { useState, useEffect, useRef } from 'react';
import axios from '../config/axiosConfig'; 
import { FaSearch, FaSpinner, FaTimes, FaUser, FaGraduationCap, FaImage, FaPalette } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';
import './SearchBar.css';

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ---- Typewriter state ----
  const [typedPlaceholder, setTypedPlaceholder] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const typingIntervalRef = useRef(null);
  const typewriterTimeoutRef = useRef(null);

  // ---- Get translated placeholder text ----
  const getFullText = () => {
    return t('searchPlaceholder') || 'Search for women, departments, activities...';
  };

  // ---- Typewriter effect ----
  useEffect(() => {
    // Clear any existing intervals/timeouts
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    if (typewriterTimeoutRef.current) {
      clearTimeout(typewriterTimeoutRef.current);
    }

    const fullText = getFullText();
    let index = 0;
    setTypedPlaceholder('');
    setIsTypingComplete(false);

    // Small delay before starting
    typewriterTimeoutRef.current = setTimeout(() => {
      typingIntervalRef.current = setInterval(() => {
        if (index < fullText.length) {
          setTypedPlaceholder((prev) => prev + fullText.charAt(index));
          index++;
        } else {
          clearInterval(typingIntervalRef.current);
          setIsTypingComplete(true);
        }
      }, 40); // 40ms per character
    }, 500);

    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
      if (typewriterTimeoutRef.current) {
        clearTimeout(typewriterTimeoutRef.current);
      }
    };
  }, [t]);

  // ---- Close results on outside click ----
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ---- Handlers ----
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setShowResults(true);
    
    try {
      const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/search', {
        params: { q: query, type: 'all' }
      });
      
      setResults(response.data);
      
    } catch (error) {
      console.error('Search error:', error);
      setResults({ 
        query: query,
        results: {},
        error: 'Search failed. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const navigateToSearchPage = () => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setShowResults(false);
      setQuery('');
    }
  };

  const navigateToItem = (category, item) => {
    setShowResults(false);
    setQuery('');
    
    switch(category) {
      case 'women':
        navigate('/#women');
        setTimeout(() => {
          const element = document.getElementById('women');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
        break;
        
      case 'departments':
        navigate('/#departments');
        setTimeout(() => {
          const element = document.getElementById('departments');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
        break;
        
      case 'activities':
        navigate(`/search?q=${encodeURIComponent(query)}&category=activities`);
        break;
        
      case 'gallery':
        navigate('/gallery');
        break;
        
      default:
        navigateToSearchPage();
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults(null);
    setShowResults(false);
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'women': return <FaUser className="search-category-icon" />;
      case 'departments': return <FaGraduationCap className="search-category-icon" />;
      case 'activities': return <FaImage className="search-category-icon" />;
      case 'gallery': return <FaPalette className="search-category-icon" />;
      default: return <FaSearch className="search-category-icon" />;
    }
  };

  const getCategoryTitle = (category) => {
    switch(category) {
      case 'women': return t('trainedWomen') || 'Trained Women';
      case 'departments': return t('trainingDepartments') || 'Training Departments';
      case 'activities': return t('activities') || 'Activities';
      case 'gallery': return t('galleryImages') || 'Gallery Images';
      default: return category;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  // ---- Determine if we should show the typed placeholder ----
  const showTypedPlaceholder = !isFocused && query.length === 0 && typedPlaceholder.length > 0 && !isTypingComplete;

  // Format the search query for display
  const getSearchQueryDisplay = () => {
    return results?.query || query;
  };

  return (
    <div className="search-container" ref={searchRef}>
      <form onSubmit={handleSearch} className="search-form">
        <div className="search-input-group">
          <div className="search-input-wrapper">
            {/* Typewriter overlay - only shown when no query and not focused */}
            {showTypedPlaceholder && (
              <div className="typewriter-overlay">
                <span className="typewriter-text">{typedPlaceholder}</span>
                <span className="typewriter-cursor">|</span>
              </div>
            )}
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isFocused || query.length > 0 ? getFullText() : ''}
              className="search-input"
              onFocus={() => {
                setIsFocused(true);
                if (results && query) {
                  setShowResults(true);
                }
              }}
              onBlur={() => setIsFocused(false)}
              aria-label={t('searchPlaceholder') || 'Search for women, departments, activities...'}
            />
          </div>
          {query && (
            <button 
              type="button" 
              onClick={clearSearch}
              className="search-clear-btn"
              aria-label="Clear search"
            >
              <FaTimes />
            </button>
          )}
          <button 
            type="submit" 
            className="search-submit-btn"
            disabled={loading || !query.trim()}
            aria-label="Search"
          >
            {loading ? <FaSpinner className="spinner" /> : <FaSearch />}
          </button>
        </div>
        
        {/* Quick Search Button */}
        {query && !loading && (
          <div className="quick-search-actions">
            <button 
              type="button"
              onClick={navigateToSearchPage}
              className="quick-search-btn"
            >
              {t('viewAllResults', { query: query }) || `View all results for "${query}"`}
            </button>
          </div>
        )}
      </form>

      {/* Search Results Dropdown */}
      {showResults && results && !results.error && (
        <div className="search-results-dropdown">
          <div className="search-results-header">
            <h4>
              {t('searchResultsFor') || 'Search Results for'} <strong>"{getSearchQueryDisplay()}"</strong>
            </h4>
            <button 
              onClick={() => setShowResults(false)}
              className="close-results-btn"
              aria-label="Close results"
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="search-results-content">
            {Object.entries(results.results || {}).map(([category, items]) => (
              items && items.length > 0 && (
                <div key={category} className="search-category-section">
                  <div className="search-category-header">
                    {getCategoryIcon(category)}
                    <h5>{getCategoryTitle(category)} ({items.length})</h5>
                  </div>
                  
                  <div className="search-category-items">
                    {items.slice(0, 3).map((item, index) => (
                      <div 
                        key={index} 
                        className="search-result-item"
                        onClick={() => navigateToItem(category, item)}
                      >
                        {category === 'women' && (
                          <>
                            <div className="search-item-image">
                              {item.photo_url ? (
                                <img src={item.photo_url} alt={item.name} />
                              ) : (
                                <div className="search-item-avatar">
                                  {item.name?.charAt(0) || 'W'}
                                </div>
                              )}
                            </div>
                            <div className="search-item-info">
                              <strong>{item.name || 'Unknown Woman'}</strong>
                              <span className="search-item-detail">{item.department_name || 'No Department'}</span>
                              <span className="search-item-detail">{item.skills || 'No skills listed'}</span>
                            </div>
                          </>
                        )}
                        
                        {category === 'departments' && (
                          <div className="search-item-info">
                            <strong>{item.name || 'Unknown Department'}</strong>
                            <p className="search-item-description">
                              {item.description?.substring(0, 100) || 'No description available'}
                            </p>
                          </div>
                        )}
                        
                        {category === 'activities' && (
                          <>
                            <div className="search-item-image">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.description} />
                              ) : (
                                <div className="search-item-icon">
                                  <FaImage />
                                </div>
                              )}
                            </div>
                            <div className="search-item-info">
                              <strong>{item.department_name || 'Activity'}</strong>
                              <p className="search-item-description">
                                {item.description?.substring(0, 80) || 'No description'}
                              </p>
                            </div>
                          </>
                        )}
                        
                        {category === 'gallery' && (
                          <>
                            <div className="search-item-image">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.title} />
                              ) : (
                                <div className="search-item-icon">
                                  <FaPalette />
                                </div>
                              )}
                            </div>
                            <div className="search-item-info">
                              <strong>{item.title || 'Untitled'}</strong>
                              <p className="search-item-description">
                                {item.description?.substring(0, 80) || 'No description'}
                              </p>
                              <span className="search-item-detail">{item.department_name}</span>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    
                    {items.length > 3 && (
                      <div 
                        className="search-view-all"
                        onClick={() => navigateToItem(category)}
                      >
                        {t('viewAllItems', { count: items.length, category: getCategoryTitle(category).toLowerCase() }) || 
                         `View all ${items.length} ${getCategoryTitle(category).toLowerCase()} →`}
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}
            
            {Object.values(results.results || {}).every(arr => !arr || arr.length === 0) && (
              <div className="search-no-results">
                <p>{t('noResultsFound', { query: getSearchQueryDisplay() }) || `No results found for "${getSearchQueryDisplay()}"`}</p>
                <p className="search-suggestions">
                  {t('trySearchingFor') || 'Try searching for:'} <em>{t('searchSuggestions') || 'tailoring, cooking, agriculture, sewing, leatherwork'}</em>
                </p>
              </div>
            )}
          </div>
          
          <div className="search-results-footer">
            <button 
              onClick={navigateToSearchPage}
              className="view-all-results-btn"
            >
              {t('viewCompleteSearchResults') || 'View Complete Search Results'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;